import { useState, useEffect } from "react";
import { Card, Stack, Field, Input, H3, Title } from "@introist/react-foundation/v2";
import styled from "styled-components";
import { useLocalStorage } from "usehooks-ts";

const CalculatorCard = styled(Card)`
  padding: 24px;
`;

const ResultCard = styled(Card)`
  background-color: #f0f7ff;
  padding: 20px;
  margin-top: 16px;
`;

interface ROICalculatorProps {
  totalWorkflows: number;
  totalSteps: number;
  onCalculate?: (result: {
    totalMinutesSaved: number;
    totalHoursSaved: number;
    totalCostSaved: number;
  }) => void;
}

export const ROICalculator = ({ totalWorkflows, totalSteps, onCalculate }: ROICalculatorProps) => {
  const [minutesPerWorkflow, setMinutesPerWorkflow] = useLocalStorage(
    "analytics.roi.minutesPerWorkflow",
    "30"
  );
  const [minutesPerStep, setMinutesPerStep] = useLocalStorage("analytics.roi.minutesPerStep", "5");
  const [costPerHour, setCostPerHour] = useLocalStorage("analytics.roi.costPerHour", "50");

  const [result, setResult] = useState<{
    totalMinutesSaved: number;
    totalHoursSaved: number;
    totalCostSaved: number;
  } | null>(null);

  useEffect(() => {
    const workflow = parseFloat(minutesPerWorkflow) || 0;
    const step = parseFloat(minutesPerStep) || 0;
    const cost = parseFloat(costPerHour) || 0;

    const totalMinutesSaved = totalWorkflows * workflow + totalSteps * step;
    const totalHoursSaved = parseFloat((totalMinutesSaved / 60).toFixed(2));
    const totalCostSaved = parseFloat((totalHoursSaved * cost).toFixed(2));

    const calculatedResult = {
      totalMinutesSaved,
      totalHoursSaved,
      totalCostSaved
    };

    setResult(calculatedResult);
    if (onCalculate) {
      onCalculate(calculatedResult);
    }
  }, [
    minutesPerWorkflow,
    minutesPerStep,
    costPerHour,
    totalWorkflows,
    totalSteps,
    onCalculate
  ]);

  return (
    <CalculatorCard>
      <Stack vertical gap="medium">
        <Title>ROI Calculator</Title>

        <Stack gap="medium" style={{ flexWrap: "wrap" }}>
          <div style={{ minWidth: "200px" }}>
            <Field title="Minutes saved per workflow activation">
              <Input
                type="number"
                value={minutesPerWorkflow}
                onChange={setMinutesPerWorkflow}
                min="0"
                step="1"
              />
            </Field>
          </div>

          <div style={{ minWidth: "200px" }}>
            <Field title="Minutes saved per step execution">
              <Input
                type="number"
                value={minutesPerStep}
                onChange={setMinutesPerStep}
                min="0"
                step="1"
              />
            </Field>
          </div>

          <div style={{ minWidth: "200px" }}>
            <Field title="Cost per hour (€)">
              <Input
                type="number"
                value={costPerHour}
                onChange={setCostPerHour}
                min="0"
                step="0.01"
              />
            </Field>
          </div>
        </Stack>

        {result && (
          <ResultCard>
            <Stack vertical gap="small">
              <Stack justifyContent="space-between">
                <span>Total time saved:</span>
                <strong>
                  {result.totalMinutesSaved.toLocaleString()} minutes ({result.totalHoursSaved.toLocaleString()}{" "}
                  hours)
                </strong>
              </Stack>
              <Stack justifyContent="space-between">
                <span>Total cost saved:</span>
                <H3 style={{ margin: 0 }}>€{result.totalCostSaved.toLocaleString()}</H3>
              </Stack>
            </Stack>
          </ResultCard>
        )}
      </Stack>
    </CalculatorCard>
  );
};
